import { Slug } from '@sanity/types';
import ShowsForCulture from 'components/culture/ShowsForCulture';
import SArticleTitle from "components/framework/SArticleTitle";
import SMaxWidthContainer from 'components/framework/SMaxWidthContainer';
import CMSImage, { OgImage } from 'components/images/Image';
import RichText, { QUERY_FRAGMENT_BODYTEXT, QUERY_FRAGMENT_INGRESS } from 'components/richtext/RichText';
import { useDocumentTitle } from 'lib/client/useTitle';
import { setDefaultCacheHeaders } from 'lib/server/cache';
import { fwpakkeQuery, getServerData } from 'lib/server/sanity';
import PublicRuntimeConfig from 'lib/types/PublicRuntimeConfig';
import { DocumentType_CULTURE, FwPakkeImageDocument, ICommonProps, ICultureArticle, IQueryResult } from 'lib/types/sanity';
import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import getConfig from 'next/config';
import Head from 'next/head';

import styles from "./KulNr.module.css";

const { publicRuntimeConfig } = getConfig() as { publicRuntimeConfig: PublicRuntimeConfig };

//#region [Other]


const PAGE_QUERY: string = `
{
	"published": *[_type =="${DocumentType_CULTURE}" && kulnr.current == $kulnr && !(_id in path("drafts.**"))] {
		...,
		${QUERY_FRAGMENT_INGRESS},
		${QUERY_FRAGMENT_BODYTEXT},
		image->,
	}[0],
	"preview": *[_type =="${DocumentType_CULTURE}" && kulnr.current == $kulnr && _id in path("drafts.**")] {
		...,
		${QUERY_FRAGMENT_INGRESS},
		${QUERY_FRAGMENT_BODYTEXT},
		image->,
	}[0]
}
`;
//#endregion

//#region [Other]getServerSideProps
export const getServerSideProps: GetServerSideProps = getServerData(async (context) => {
    const kulnr = context.params?.kulnr;
    if (!kulnr) {
        return { notFound: true };
    }

    setDefaultCacheHeaders(context as GetServerSidePropsContext);

    const pageData = await fwpakkeQuery<IQueryResult<ICultureArticle>>(PAGE_QUERY, { kulnr });

    const ctx = context as GetServerSidePropsContext;
    const isP = ctx.query?.p === "1" && context.preview;
    const document = isP ? (pageData?.preview ?? pageData?.published) : pageData?.published;

    if (!document) {
        console.warn("Article not found", kulnr);
        return { notFound: true };
    }

    if (!isP && (document.kulnr as Slug)?.current !== kulnr) {
        console.warn("Article found, but slug does not match latest published slug", kulnr);
        return { notFound: true };
    }

    return {
        props: {
            document,
        }
    };

});
//#endregion

//#region [Props]
type NewsProps = {
    document: ICultureArticle;
} & ICommonProps;
//#endregion

//#region [Component]
export default function CultureArticle({ document, appData }: NewsProps) {
    const title = useDocumentTitle(appData.settings, document);

    return (<SMaxWidthContainer as="article">
        <Head>
            <title>{title}</title>
            <meta property="og:title" content={title} key="title" />
            <meta property="og:url" content={`${publicRuntimeConfig?.NEXT_PUBLIC_BASE_URL}kulturprogram/${document?.kulnr.current}`} key="ogUrl" />
        </Head>
        <OgImage document={document} />

        <SArticleTitle>{document.titleOverride ?? document.title}</SArticleTitle>

        {document.image && <CMSImage className={styles.SArticleImage} width={640} image={document.image as FwPakkeImageDocument} sizes="(min-width: 1440px) 1440px, 100vw" />}

        <ShowsForCulture kulnr={document.kulnr.current} nynorsk={appData.settings.nynorsk} />

        <SMaxWidthContainer className={styles.STextContainer}>
            <RichText className={styles.SIngress} value={document.ingress} />
            <RichText value={document.bodyText} />
        </SMaxWidthContainer>

    </SMaxWidthContainer>);
}
//#endregion